"use client";

import { useEffect, useRef } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { CONSENT_KEY, useConsent } from "@/lib/useConsent";

/**
 * GA4 + Meta Pixel. Each loads only when its id is set, so local dev and preview builds
 * stay silent until the ids land in the environment:
 *
 *   NEXT_PUBLIC_GA_ID          G-XXXXXXXXXX  (GA4 measurement id)
 *   NEXT_PUBLIC_META_PIXEL_ID  numeric pixel id
 *
 * GA4 runs in Consent Mode v2 (advanced) for every visitor: it starts with all four
 * consent types denied, which means no cookies and only cookieless pings, so Google's tag
 * check finds the tag and GA can model the visitors who never accept. Accepting in the
 * banner updates consent to granted; declining keeps it denied. The Meta Pixel has no
 * cookieless mode, so it still loads only after a visitor accepts.
 *
 * Besides pageviews, one listener covers mailto clicks, so no component needs its own
 * onClick: the pre-filled consultation link (it carries a subject) fires GA
 * "generate_lead" / Meta "Lead"; the plain email link fires "contact" / "Contact".
 * Google's booking embed sends nothing back to the page, so bookings and questionnaire
 * submissions are sent server side by the Apps Script in apps-script/tracking/.
 */
const GA_ID = process.env.NEXT_PUBLIC_GA_ID;
const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

export const consentState = (value: "granted" | "denied") => ({
  ad_storage: value,
  ad_user_data: value,
  ad_personalization: value,
  analytics_storage: value,
});

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

export const Analytics = () => {
  const consent = useConsent();
  const granted = consent === "granted";
  const pathname = usePathname();

  /**
   * Keeps Google's consent state in step with the banner. The init script below sets the
   * state for the page load from the stored answer; this covers answers given (or
   * changed) afterwards. Until the init script has run there is no gtag to tell, and the
   * init script reads the stored answer itself.
   *
   * Undecided (null) changes nothing: it is also what the hydration render reports before
   * the stored answer is read, and telling Google "denied" then would undo a returning
   * visitor's grant. A withdrawal through the footer's Cookies link reopens the banner and
   * Decline sends the denial.
   */
  useEffect(() => {
    if (consent === null) return;
    window.gtag?.("consent", "update", consentState(consent));
  }, [consent]);

  /**
   * App Router navigations do not reload the page, so the Pixel's one-time PageView only
   * ever counts the entry. GA4 needs nothing here: its enhanced measurement already sends
   * a page_view on every history change.
   *
   * Skips the first run: the Pixel already counts the page it loads on.
   */
  const firstRun = useRef(true);
  useEffect(() => {
    if (!granted) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    window.fbq?.("track", "PageView");
  }, [granted, pathname]);

  useEffect(() => {
    if (!GA_ID && !PIXEL_ID) return;
    // Not gated on consent: without it GA4 sends a cookieless ping and the Pixel is absent.
    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest?.("a[href^='mailto:']");
      if (!link) return;
      const isConsult = (link.getAttribute("href") ?? "").includes("subject=");
      window.gtag?.("event", isConsult ? "generate_lead" : "contact", { method: "email" });
      window.fbq?.("track", isConsult ? "Lead" : "Contact");
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return (
    <>
      {GA_ID && (
        <>
          <Script id="ga-init" strategy="afterInteractive">
            {/* Consent Mode v2. The denied default has to be pushed before config; a
                visitor who already accepted on an earlier visit is granted straight away,
                so their first pageview is counted with cookies. */}
            {`window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent', 'default', ${JSON.stringify({ ...consentState("denied"), wait_for_update: 500 })});
              try {
                if (localStorage.getItem('${CONSENT_KEY}') === 'granted') {
                  gtag('consent', 'update', ${JSON.stringify(consentState("granted"))});
                }
              } catch (e) {}
              gtag('js', new Date());
              gtag('config', '${GA_ID}');`}
          </Script>
          <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} strategy="afterInteractive" />
        </>
      )}
      {granted && PIXEL_ID && (
        <>
          <Script id="meta-pixel" strategy="afterInteractive">
            {`!function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${PIXEL_ID}');
              fbq('track', 'PageView');`}
          </Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}
    </>
  );
};
